import { CollectionInvoiceDocument } from '../model';

export const UNGROUPED_LABEL = 'Ungrouped';

export interface DepartmentInvoiceGroup {
  label: string;
  isUngrouped: boolean;
  invoices: CollectionInvoiceDocument[];
  count: number;
  total: number;
}

// Multi-department invoices intentionally appear in EACH of their departments,
// so per-department totals can sum to more than the client's total past due.
// Department-less invoices collect into a single "Ungrouped" bucket.
export const groupInvoicesByDepartment = (
  documents: CollectionInvoiceDocument[],
): DepartmentInvoiceGroup[] => {
  const buckets = new Map<string, CollectionInvoiceDocument[]>();

  const addTo = (label: string, doc: CollectionInvoiceDocument) => {
    const existing = buckets.get(label) ?? [];
    existing.push(doc);
    buckets.set(label, existing);
  };

  documents.forEach((doc) => {
    const departments = doc.department_groups ?? [];
    if (departments.length === 0) {
      addTo(UNGROUPED_LABEL, doc);
      return;
    }
    departments.forEach((dept) => addTo(dept, doc));
  });

  const groups: DepartmentInvoiceGroup[] = Array.from(buckets.entries()).map(([label, invoices]) => ({
    label,
    isUngrouped: label === UNGROUPED_LABEL,
    invoices,
    count: invoices.length,
    total: invoices.reduce((sum, inv) => sum + inv.amount, 0),
  }));

  // Named departments alphabetically; "Ungrouped" always pinned last.
  return groups.sort((a, b) => {
    if (a.isUngrouped) return 1;
    if (b.isUngrouped) return -1;
    return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  });
};

export const hasMultiDepartmentInvoice = (documents: CollectionInvoiceDocument[]): boolean =>
  documents.some((doc) => (doc.department_groups ?? []).length > 1);
